import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './AdminLogin.css';
import { useAdminAccess } from '../../context/AdminAccessContext';
import { getStoredAdminRoute, DEFAULT_ADMIN_ROUTE } from '../../constants/adminNavigation';

const AdminLogin = () => {
  const { adminEnabled, loading, isAuthenticated, login } = useAdminAccess();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTarget = useMemo(() => {
    if (location.state?.from?.pathname) {
      return location.state.from.pathname;
    }
    return getStoredAdminRoute() || DEFAULT_ADMIN_ROUTE;
  }, [location.state]);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, redirectTarget]);

  const updateField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setFieldErrors({});

    try {
      await login({ username: form.username.trim(), password: form.password });
    } catch (apiError) {
      const responseErrors = apiError?.response?.data?.details || {};
      setFieldErrors(responseErrors);
      const message = apiError?.response?.data?.message || 'Unable to sign in. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!loading && !adminEnabled) {
    return (
      <div className="admin-login-shell">
        <div className="admin-login-card">
          <h1>Admin access disabled</h1>
          <p>The admin console is currently turned off in settings.</p>
          <Link to="/" className="admin-login-link">Return home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-shell">
      <form className="admin-login-card" onSubmit={handleSubmit}>
        <div>
          <h1>IFStatic Admin</h1>
          <p>Sign in to continue</p>
        </div>

        {error && <div className="admin-login-error">{error}</div>}

        <label>
          <span>Username</span>
          <input
            type="text"
            value={form.username}
            onChange={updateField('username')}
            disabled={submitting || loading}
            autoComplete="username"
            required
          />
          {Array.isArray(fieldErrors.username) && fieldErrors.username[0] && (
            <small>{fieldErrors.username[0]}</small>
          )}
        </label>

        <label>
          <span>Password</span>
          <input
            type="password"
            value={form.password}
            onChange={updateField('password')}
            disabled={submitting || loading}
            autoComplete="current-password"
            required
          />
          {Array.isArray(fieldErrors.password) && fieldErrors.password[0] && (
            <small>{fieldErrors.password[0]}</small>
          )}
        </label>

        <button type="submit" disabled={submitting || loading}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

export default AdminLogin;
